// SatelliteInfoCard — right-side card that opens when the user clicks
// one of the project satellites in the Projects scene. Shows the
// project's name, blurb, tech stack, and year. Also shows a list of
// available satellites when no individual one is selected.

import { useEffect, useState } from 'react';
import {
  SATELLITES,
  type SatelliteConfig,
  type SceneConfig,
} from './planetSections';
import { Magnetic } from './Magnetic';
import { openDetail } from './SatelliteInteraction';

// ---------------------------------------------------------------------------
// Subscribers — the planet scene emits a satellite:open event with the
// satellite id. We listen and reflect it in React state.
// ---------------------------------------------------------------------------

interface SatelliteOpenDetail {
  id: string;
}

declare global {
  interface WindowEventMap {
    'satellite:open': CustomEvent<SatelliteOpenDetail>;
    'satellite:close': CustomEvent<null>;
  }
}

export interface SatelliteInfoCardProps {
  activeScene: SceneConfig;
  /** True when a panel for this scene is open (from the controller). */
  openByDefault?: boolean;
}

export function SatelliteInfoCard({
  activeScene,
  openByDefault,
}: SatelliteInfoCardProps) {
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<SatelliteOpenDetail>).detail;
      setOpenId(detail?.id ?? null);
    };
    const onClose = () => setOpenId(null);
    if (typeof window !== 'undefined') {
      window.addEventListener('satellite:open', onOpen as EventListener);
      window.addEventListener('satellite:close', onClose as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('satellite:open', onOpen as EventListener);
        window.removeEventListener('satellite:close', onClose as EventListener);
      }
    };
  }, []);

  const satellite = openId
    ? SATELLITES.find((s) => s.id === openId) ?? null
    : null;

  const isOpen = !!satellite || (openByDefault && SATELLITES.length > 0);

  return (
    <aside
      className={['sat-card', isOpen ? 'is-open' : ''].join(' ')}
      aria-label="Project satellite"
      aria-hidden={!isOpen}
    >
      {satellite ? (
        <SatelliteDetail satellite={satellite} onClose={() => setOpenId(null)} />
      ) : (
        <SatelliteList
          onPick={(id) => setOpenId(id)}
          activeScene={activeScene}
        />
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// SatelliteDetail — the open card for one project.
// ---------------------------------------------------------------------------

function SatelliteDetail({
  satellite,
  onClose,
}: {
  satellite: SatelliteConfig;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        position: 'relative',
      }}
    >
      <Magnetic
        as="button"
        className="hud-panel__close"
        ariaLabel="Close satellite detail"
        onClick={onClose}
        type="button"
        innerClassName="hud-panel__close-inner"
      >
        <span aria-hidden="true">×</span>
      </Magnetic>
      <div className="hud-panel__kicker">Project</div>
      <h3
        className="hud-panel__title"
        style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: 3,
            background: satellite.color,
            boxShadow: `0 0 12px ${satellite.color}40`,
          }}
          aria-hidden="true"
        />
        {satellite.name}
      </h3>
      <p
        className="hud-panel__year"
        style={{ marginBottom: 12, display: 'inline-block' }}
      >
        {satellite.year}
      </p>
      <p className="hud-panel__body">{satellite.blurb}</p>
      <div className="hud-panel__divider" />
      <div className="hud-panel__tags">
        {satellite.tech.map((t) => (
          <Magnetic
            key={t}
            as="span"
            className="hud-panel__tag"
            innerClassName="hud-panel__tag-inner"
            ariaLabel={`Stack: ${t}`}
          >
            {t}
          </Magnetic>
        ))}
      </div>
      <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <Magnetic
          as="button"
          className="hud-panel__readmore"
          ariaLabel="Open project deep-dive"
          type="button"
          onClick={() => openDetail({ type: 'project', id: satellite.id })}
          innerClassName="hud-panel__readmore-inner"
        >
          <span>Deep dive</span>
          <span aria-hidden="true" className="hud-panel__readmore-arrow">→</span>
        </Magnetic>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SatelliteList — small grid of available satellites shown when no
// individual satellite is picked.
// ---------------------------------------------------------------------------

function SatelliteList({
  onPick,
  activeScene,
}: {
  onPick: (id: string) => void;
  activeScene: SceneConfig;
}) {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      }}
    >
      <div className="hud-panel__kicker">Projects</div>
      <h3 className="hud-panel__title">{activeScene.title}</h3>
      <p className="hud-panel__subtitle">{activeScene.subtitle}</p>
      <div className="hud-panel__divider" />
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
        {SATELLITES.map((s) => (
          <li key={s.id}>
            <Magnetic
              as="button"
              className="sat-row"
              ariaLabel={`Open ${s.name}`}
              onClick={() => onPick(s.id)}
              type="button"
              innerClassName="sat-row-inner"
            >
              <span
                aria-hidden="true"
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: s.color,
                  boxShadow: `0 0 8px ${s.color}40`,
                  flexShrink: 0,
                }}
              />
              <span style={{ flex: 1, fontFamily: 'var(--font-display)', fontSize: 16, textAlign: 'left' }}>
                {s.name}
              </span>
              <span
                className="t-mono"
                style={{
                  fontSize: 10,
                  color: 'var(--text-muted)',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                }}
              >
                {s.year}
              </span>
            </Magnetic>
          </li>
        ))}
      </ul>
    </div>
  );
}
